import DateRangeSelection, {
  DateRangeSelection$ChangeEvent,
} from "sap/m/DateRangeSelection";
import { MetadataOptions } from "sap/ui/base/ManagedObject";
import { ValueState } from "sap/ui/core/library";
import { GetDateOnlyString } from "../utils/FormatUtil";
import UI5Date from "sap/ui/core/date/UI5Date";

/**
 * Extends DateRangeSelection to show validation error when user manually enters a badly formatted date range.
 * Also enables binding to date strings like "yyyy-MM-dd", which is what .NET accepts as DateOnly.
 * Also enables parsing "dateValue" and "secondDateValue" from string.
 * If you want to bind to a DateOnly value, use "strValueFrom" and "strValueTo" properties.
 * If you want to bind to a DateTime value relative to the current time zone, use "dateValue" and "secondDateValue" properties.
 * @namespace akademika.akamont.customControls
 */
export default class CustomDateRangeSelection extends DateRangeSelection {
  static readonly metadata: MetadataOptions = {
    properties: {
      strValueFrom: { type: "string" }, // stringified DateOnly value from backend as "yyyy-MM-dd"
      strValueTo: { type: "string" }, // stringified DateOnly value from backend as "yyyy-MM-dd"
    },
  };

  public setStrValueFrom(sDateValue?: string): this {
    this.setProperty("strValueFrom", sDateValue, true);
    return super.setDateValue(this.parseDate(sDateValue));
  }

  public setStrValueTo(sDateValue?: string): this {
    this.setProperty("strValueTo", sDateValue, true);
    return super.setSecondDateValue(this.parseDate(sDateValue));
  }

  public setDateValue(oDateValue: Date | UI5Date | string | null): this {
    if (typeof oDateValue === "string") {
      oDateValue = new Date(oDateValue);
    }
    return super.setDateValue(oDateValue);
  }

  public setSecondDateValue(oDateValue: Date | UI5Date | string | null): this {
    if (typeof oDateValue === "string") {
      oDateValue = new Date(oDateValue);
    }
    return super.setSecondDateValue(oDateValue);
  }

  private parseDate(sDateValue?: string): Date {
    // we have to fool typescript here, because super's setter function type doesn't accept undefined or null
    if (!sDateValue) return undefined as unknown as Date;

    return new Date(sDateValue);
  }

  public init(): void {
    super.init();
    this.attachChange(this.handleDateChange);
  }

  private handleDateChange(event: DateRangeSelection$ChangeEvent) {
    if (event.getParameter("valid")) {
      this.setValueState(ValueState.None);
    } else {
      this.setValueState(ValueState.Error);
    }

    const from = event.getParameter("from") as Date;
    const to = event.getParameter("to") as Date;

    this.setProperty("strValueFrom", GetDateOnlyString(from), true);
    this.setProperty("strValueTo", GetDateOnlyString(to), true);
  }

  static renderer = { apiVersion: 2 };
}
